import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import type { WritableComputedRef } from 'vue'
import { NOOP, initAliveStoreIds, kapixContext, remove } from 'kapix-components-vue3'
import pFormationMatchingComponentStoreCustom from './store.custom'

const storeName = 'pFormationMatchingComponent'
const customImplement: CustomStoreImplementationOptions = pFormationMatchingComponentStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function pFormationMatchingComponentFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        $parentPage: NOOP,
        $parentComponent: NOOP,
        // Constants,
        $constants: {},
        // Data,
        $data: {
          descriptionCompany: undefined as Nullable<string>,
          expertText: undefined as Nullable<string>,
          remainsDependent: undefined as Nullable<string>,
          budgetAvailable: undefined as Nullable<string>,
          trainingHours: undefined as Nullable<string>,
          supportDuration: undefined as Nullable<string>,
          photoExpert: undefined as Nullable<string | File>,
          descriptionImageAlt: undefined as Nullable<string>,
          image: undefined as Nullable<string | File>
        },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      /* @ts-ignore: to allow override in your custom file */
      $descriptionCompany (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.descriptionCompany,
          set: (value) => {
            this.$data.descriptionCompany = value
            this.$emit('update:descriptionCompany', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $expertText (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.expertText,
          set: (value) => {
            this.$data.expertText = value
            this.$emit('update:expertText', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $remainsDependent (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.remainsDependent,
          set: (value) => {
            this.$data.remainsDependent = value
            this.$emit('update:remainsDependent', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $budgetAvailable (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.budgetAvailable,
          set: (value) => {
            this.$data.budgetAvailable = value
            this.$emit('update:budgetAvailable', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $trainingHours (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.trainingHours,
          set: (value) => {
            this.$data.trainingHours = value
            this.$emit('update:trainingHours', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $supportDuration (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.supportDuration,
          set: (value) => {
            this.$data.supportDuration = value
            this.$emit('update:supportDuration', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $photoExpert (): WritableComputedRef<Nullable<string | File>> {
        return computed({
          get: () => this.$data.photoExpert,
          set: (value) => {
            this.$data.photoExpert = value
            this.$emit('update:photoExpert', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $descriptionImageAlt (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.descriptionImageAlt,
          set: (value) => {
            this.$data.descriptionImageAlt = value
            this.$emit('update:descriptionImageAlt', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $image (): WritableComputedRef<Nullable<string | File>> {
        return computed({
          get: () => this.$data.image,
          set: (value) => {
            this.$data.image = value
            this.$emit('update:image', value)
          }
        })
      },
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return pFormationMatchingComponentFactory(storeId)()
}

export const pFormationMatchingComponentRaw = pFormationMatchingComponentFactory()
export const pFormationMatchingComponent = () => pFormationMatchingComponentRaw() as MergeCustomStore<typeof pFormationMatchingComponentStoreCustom.instance>
